import React, { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';
import './DeviationsView.css';

const DEVIATION_COLORS = {
  'normal': '#28a745',
  'warning': '#ffc107',
  'critical': '#dc3545',
  'over-allocated': '#6f42c1'
};

const DeviationsView = ({ deviationData }) => {
  const [deviationFilter, setDeviationFilter] = useState('all');

  const getFilteredDeviations = () => {
    if (deviationFilter === 'all') return deviationData;
    if (deviationFilter === 'over-count') return deviationData.filter(d => d.countDeviation > 10);
    if (deviationFilter === 'over-allocation') return deviationData.filter(d => d.allocationDeviation > 5);
    if (deviationFilter === 'both') return deviationData.filter(d => d.countDeviation > 10 && d.allocationDeviation > 5);
    return deviationData;
  };

  return (
    <div className="deviations-view">
      {/* Deviation Controls */}
      <div className="deviation-controls">
        <h3>🚨 Deviation Analysis</h3>
        <div className="deviation-filters">
          <button 
            className={`deviation-filter-btn all ${deviationFilter === 'all' ? 'active' : ''}`}
            onClick={() => setDeviationFilter('all')}
          >
            📊 All Deviations ({deviationData.length})
          </button>
          <button 
            className={`deviation-filter-btn count ${deviationFilter === 'over-count' ? 'active' : ''}`}
            onClick={() => setDeviationFilter('over-count')}
          >
            👥 Over Count ({deviationData.filter(d => d.countDeviation > 10).length})
          </button>
          <button 
            className={`deviation-filter-btn allocation ${deviationFilter === 'over-allocation' ? 'active' : ''}`}
            onClick={() => setDeviationFilter('over-allocation')}
          >
            💰 Over Allocation ({deviationData.filter(d => d.allocationDeviation > 5).length})
          </button>
          <button 
            className={`deviation-filter-btn both ${deviationFilter === 'both' ? 'active' : ''}`}
            onClick={() => setDeviationFilter('both')}
          >
            ⚠️ Both Issues ({deviationData.filter(d => d.countDeviation > 10 && d.allocationDeviation > 5).length})
          </button>
        </div>
      </div>

      {/* Deviation Overview Table */}
      <div className="chart-section full-width">
        <h2>🚨 Project-Level Billable Count & Allocation Deviations</h2>
        {getFilteredDeviations().length > 0 ? (
          <div className="deviation-table">
            <table>
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Current Billable Count</th>
                  <th>Allowed Count</th>
                  <th>Count Deviation (%)</th>
                  <th>Billable Ratio (%)</th>
                  <th>Current Allocation (%)</th>
                  <th>Max Allocation (%)</th>
                  <th>Allocation Deviation (%)</th>
                  <th>Status</th>
                  <th>Total Resources</th>
                  <th>Action Required</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredDeviations().map((deviation, index) => (
                  <tr key={index} className={`deviation-row ${deviation.deviationType}`}>
                    <td className="project-name">{deviation.project}</td>
                    <td className="billable-count">{deviation.currentBillableCount}</td>
                    <td className="allowed-count">{deviation.allowedCount}</td>
                    <td className={`count-deviation ${deviation.countDeviation > 20 ? 'critical' : deviation.countDeviation > 10 ? 'warning' : 'normal'}`}>
                      {deviation.countDeviation > 0 ? '+' : ''}{deviation.countDeviation.toFixed(1)}%
                    </td>
                    <td className="billable-ratio">{deviation.billablePercentage}%</td>
                    <td className="current-allocation">{deviation.currentTotalAllocation.toFixed(1)}%</td>
                    <td className="max-allocation">{deviation.maxAllocation.toFixed(1)}%</td>
                    <td className={`allocation-deviation ${deviation.allocationDeviation > 10 ? 'critical' : deviation.allocationDeviation > 5 ? 'warning' : 'normal'}`}>
                      {deviation.allocationDeviation > 0 ? '+' : ''}{deviation.allocationDeviation.toFixed(1)}%
                    </td>
                    <td>
                      <span className={`severity-badge ${deviation.deviationType}`}>
                        {deviation.deviationType?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="total-resources">{deviation.totalResources}</td>
                    <td className="action-required">{deviation.deviationMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-deviations">
            <div className="empty-state">
              <h3>✅ No deviations found!</h3>
              <p>All projects are within their allowed count and allocation limits.</p>
            </div>
          </div>
        )}
      </div>

      {/* Deviation Summary Charts */}
      <div className="chart-section">
        <h2>Deviation Distribution</h2>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={[
                { name: 'Normal', value: deviationData.filter(d => d.deviationType === 'normal').length, fill: DEVIATION_COLORS.normal },
                { name: 'Warning', value: deviationData.filter(d => d.deviationType === 'warning').length, fill: DEVIATION_COLORS.warning },
                { name: 'Critical', value: deviationData.filter(d => d.deviationType === 'critical').length, fill: DEVIATION_COLORS.critical }
              ]}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, value }) => `${name}: ${value}`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {[
                { name: 'Normal', value: deviationData.filter(d => d.deviationType === 'normal').length, fill: DEVIATION_COLORS.normal },
                { name: 'Warning', value: deviationData.filter(d => d.deviationType === 'warning').length, fill: DEVIATION_COLORS.warning },
                { name: 'Critical', value: deviationData.filter(d => d.deviationType === 'critical').length, fill: DEVIATION_COLORS.critical }
              ].map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Count vs Allocation Scatter Plot */}
      <div className="chart-section">
        <h2>Count vs Allocation Deviation Analysis</h2>
        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid />
            <XAxis type="number" dataKey="countDeviation" name="Count Deviation (%)" />
            <YAxis type="number" dataKey="allocationDeviation" name="Allocation Deviation (%)" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} 
              formatter={(value, name) => [
                `${value.toFixed(1)}%`, 
                name === 'countDeviation' ? 'Count Deviation' : 'Allocation Deviation'
              ]}
            />
            <Scatter 
              name="Deviations" 
              data={deviationData} 
              fill="#dc3545"
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DeviationsView; 